/**
 * Обёртка над Telegram Mini Apps SDK.
 *
 * Работаем напрямую с window.Telegram.WebApp, без npm-пакета. Причина
 * не в экономии: SDK всё равно приходит скриптом от Telegram, и обёртка
 * над ним нужна ровно одна — эта. Лишний слой добавил бы вторую версию
 * тех же типов, которая начнёт расходиться с реальностью.
 *
 * Весь остальной код приложения не должен знать слова Telegram.
 */

import { atLeast } from './version'

export interface TelegramUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  language_code?: string
  is_premium?: boolean
}

/** События Telegram, на которые мы подписываемся. Список намеренно закрытый. */
export type TelegramEvent =
  | 'activated'
  | 'deactivated'
  | 'deviceOrientationChanged'
  | 'deviceOrientationFailed'
  | 'viewportChanged'

export interface DeviceOrientationAPI {
  isStarted: boolean
  alpha: number | null
  beta: number | null
  gamma: number | null
  start: (
    params: { refresh_rate?: number; need_absolute?: boolean },
    callback?: (started: boolean) => void,
  ) => void
  stop: (callback?: (stopped: boolean) => void) => void
}

export interface WebApp {
  initData: string
  initDataUnsafe: { user?: TelegramUser; start_param?: string }
  colorScheme: 'light' | 'dark'
  version: string
  platform: string
  isExpanded: boolean
  /** Bot API 8.0. Свёрнутый в док мини-апп остаётся visible, но не активен. */
  isActive?: boolean
  DeviceOrientation?: DeviceOrientationAPI
  onEvent?: (event: TelegramEvent, cb: (payload?: unknown) => void) => void
  offEvent?: (event: TelegramEvent, cb: (payload?: unknown) => void) => void
  ready: () => void
  expand: () => void
  close: () => void
  setHeaderColor: (color: string) => void
  setBackgroundColor: (color: string) => void
  setBottomBarColor?: (color: string) => void
  disableVerticalSwipes?: () => void
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void
    selectionChanged: () => void
  }
  BackButton: {
    show: () => void
    hide: () => void
    onClick: (cb: () => void) => void
    offClick: (cb: () => void) => void
  }
}

declare global {
  interface Window {
    Telegram?: { WebApp?: WebApp }
  }
}

const app = (): WebApp | undefined => window.Telegram?.WebApp

/**
 * Доступ к сырому объекту для соседних модулей пакета.
 *
 * Наружу из @taro/tg он не торчит: как только приложение получит WebApp
 * напрямую, обёртка перестанет быть границей и слово Telegram расползётся
 * по экранам.
 */
export const webApp = app

/** Поддерживает ли клиент версию Bot API. См. version.ts. */
export const supports = (minVersion: string): boolean => {
  const v = app()?.version
  return Boolean(v) && atLeast(v as string, minVersion)
}

/**
 * Запущены ли мы внутри Telegram.
 *
 * Проверяем не наличие объекта, а наличие initData: скрипт Telegram
 * подключается и в обычном браузере, объект там есть, а данных нет.
 * Без этой разницы приложение снаружи выглядит сломанным вместо того,
 * чтобы честно сказать «открой в Telegram».
 */
export const isInsideTelegram = (): boolean => Boolean(app()?.initData)

/** Сырые initData для заголовка запроса. Проверяет их сервер, не мы. */
export const initDataRaw = (): string => app()?.initData ?? ''

export const telegramUser = (): TelegramUser | undefined => app()?.initDataUnsafe?.user

/** Параметр из deep-link: ?startapp=... */
export const startParam = (): string | undefined => app()?.initDataUnsafe?.start_param

/**
 * Таймзона пользователя.
 *
 * Telegram её не отдаёт, поэтому берём из браузера и отправляем на сервер.
 * Без неё «карта дня» и «один раз в сутки» считаются по времени сервера,
 * то есть неправильно для всех, кто не в его часовом поясе.
 */
export const browserTimezone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

export interface ReadyOptions {
  /** Цвет фона приложения — красим в него нативный хром, чтобы не было шва. */
  backgroundColor?: string
}

/**
 * Сообщить Telegram, что приложение готово, и настроить окно.
 *
 * disableVerticalSwipes критичен: без него вертикальная составляющая
 * жеста при тасовке колоды закрывает мини-апп. Метод появился не сразу,
 * поэтому вызов защищён.
 */
export function ready(opts: ReadyOptions = {}): void {
  const tg = app()
  if (!tg) return

  tg.ready()
  tg.expand()
  tg.disableVerticalSwipes?.()

  if (opts.backgroundColor) {
    // Наследование только в одну сторону: мы push'им наши цвета в нативный
    // хром, но themeParams себе не берём. Приложение продаёт атмосферу,
    // и перекраска в цвета мессенджера её разрушает.
    tg.setHeaderColor(opts.backgroundColor)
    tg.setBackgroundColor(opts.backgroundColor)
    tg.setBottomBarColor?.(opts.backgroundColor)
  }
}

export type HapticKind = 'tap' | 'select' | 'success' | 'warning' | 'error'

/**
 * Вибрация.
 *
 * Ровно пять поводов, и success — единственный на весь сценарий расклада.
 * Вибрация, которая срабатывает часто, перестаёт что-либо значить.
 */
export function haptic(kind: HapticKind): void {
  const h = app()?.HapticFeedback
  if (!h) return

  switch (kind) {
    case 'tap':
      h.impactOccurred('light')
      break
    case 'select':
      h.selectionChanged()
      break
    case 'success':
      h.notificationOccurred('success')
      break
    case 'warning':
      h.notificationOccurred('warning')
      break
    case 'error':
      h.notificationOccurred('error')
      break
  }
}

/** Системная кнопка «назад». Возвращает функцию отписки. */
export function backButton(onBack: () => void): () => void {
  const tg = app()
  if (!tg) return () => {}

  tg.BackButton.onClick(onBack)
  tg.BackButton.show()

  return () => {
    tg.BackButton.offClick(onBack)
    tg.BackButton.hide()
  }
}

/**
 * Подсказка о предпочтениях пользователя при ПЕРВОМ запуске.
 *
 * Именно подсказка: дальше тему выбирает человек, и переключение темы
 * в мессенджере посреди сессии не должно переворачивать палитру во время
 * анимации переворота карты.
 */
export const prefersLight = (): boolean => app()?.colorScheme === 'light'
